import { getSession, SessionContextValue, signOut } from 'next-auth/react';
import { notFound } from 'next/navigation';
import { API_URL, LS_LANG_KEY } from '@/constants.ts';

type ApiErrorData = {
  errors: Record<string, string[]>,
};

export class ApiError extends Error {
  status: number;

  data?: ApiErrorData;

  constructor(message: string, status: number, data: ApiErrorData) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

export enum RequestType {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  DELETE = 'delete',
  PATCH = 'patch',
}

export type ApiClient = {
  headers: Record<string, string>,
  authorize: boolean,
  withoutAuth(): ApiClient,
  request<ResponseType = Response>(
    session: SessionContextValue,
    url: string,
    method: RequestType,
    successfulStatus: number,
    params?: RequestInit
  ): Promise<{ statusCode: number, response: ResponseType }>,
  get<ResponseType = Response>(session: SessionContextValue, url: string, successfulStatus: number, params?: RequestInit):
  Promise<{ statusCode: number, response: ResponseType }>,
  post<ResponseType = Response>(session: SessionContextValue, url: string, successfulStatus: number, params?: RequestInit):
  Promise<{ statusCode: number, response: ResponseType }>,
  put<ResponseType = Response>(session: SessionContextValue, url: string, successfulStatus: number, params?: RequestInit):
  Promise<{ statusCode: number, response: ResponseType }>,
  patch<ResponseType = Response>(session: SessionContextValue, url: string, successfulStatus: number, params?: RequestInit):
  Promise<{ statusCode: number, response: ResponseType }>,
  delete<ResponseType = Response>(session: SessionContextValue, url: string, successfulStatus: number, params?: RequestInit):
  Promise<{ statusCode: number, response: ResponseType }>,
  download(session: SessionContextValue, url: string, accept: string, fallbackName: string, successfulStatus: number):
  Promise<void>,
};

const appendFormData = (formData: FormData, key: string, value: unknown) => {
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      appendFormData(formData, `${key}[${index}]`, item);
    });
  } else if (value instanceof File) {
    formData.append(key, value);
  } else if (typeof value === 'object' && value !== null) {
    formData.append(key, JSON.stringify(value));
  } else {
    formData.append(key, `${value}`);
  }
};

export const convertToFormData = (data: Record<string, unknown>) => Object.entries(data)
  .filter(([, value]) => value !== undefined && value !== null && value !== '')
  .reduce((formData, [key, value]) => {
    appendFormData(formData, key, value);
    return formData;
  }, new FormData());

export const convertToFormDataV2 = (data: Record<string, unknown>) => Object.entries(data)
  .filter(([, value]) => value !== undefined)
  .reduce((formData, [key, value]) => {
    appendFormData(formData, key, value);
    return formData;
  }, new FormData());

function getUrlWithBase(url: string) {
  return `${API_URL}/${url}`;
}

const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

const apiClient: ApiClient = {
  headers: { ...defaultHeaders },
  authorize: true,

  withoutAuth() {
    const client = Object.create(this);
    client.authorize = false;
    return client;
  },

  async request<ResponseType = Response>(
    session: SessionContextValue,
    url: string,
    method: RequestType,
    successfulStatus: number,
    params?: RequestInit,
  ) {
    const headers = { ...this.headers };
    if (params?.body instanceof FormData) {
      delete headers['Content-Type'];
    }

    if (this.authorize) {
      if (session?.data?.user?.accessToken) {
        headers.Authorization = `Bearer ${session.data.user.accessToken}`;
      } else {
        const sessionResponse = await getSession();
        headers.Authorization = `Bearer ${sessionResponse?.user.accessToken}`;
      }
    }

    const acceptedLanguage = localStorage.getItem(LS_LANG_KEY);
    if (acceptedLanguage) {
      headers['Accept-Language'] = acceptedLanguage;
    }
    const response = await fetch(getUrlWithBase(url), {
      method,
      ...params,
      headers: {
        ...headers,
        ...params?.headers,
      },
    });

    if (response.status === 404) {
      if (typeof window === 'undefined') {
        return notFound();
      } else {
        window.location.href = '/404';
      }
    }

    if (response.status === 403) {
      await signOut();
    }

    if (response.status === 401) {
      await signOut();
      localStorage.removeItem('currentParsingFiles');
    }

    if (response.status !== successfulStatus) {
      return response.json().then(errorData => {
        throw new ApiError(errorData?.error?.message || errorData?.message || 'An error occurred', response.status, errorData);
      });
    }

    const isJson = response.headers.get('content-type')?.startsWith('application/json');
    return { statusCode: response.status, response: (isJson ? await response.json() : response) as ResponseType };
  },

  async get<ResponseType = Response>(session: SessionContextValue, url: string, successfulStatus: number, params?: RequestInit) {
    return this.request<ResponseType>(session, url, RequestType.GET, successfulStatus, params);
  },
  async delete<ResponseType = Response>(session: SessionContextValue, url: string, successfulStatus: number, params?: RequestInit) {
    return this.request<ResponseType>(session, url, RequestType.DELETE, successfulStatus, params);
  },
  async post<ResponseType = Response>(session: SessionContextValue, url: string, successfulStatus: number, params?: RequestInit) {
    return this.request<ResponseType>(session, url, RequestType.POST, successfulStatus, params);
  },
  async put<ResponseType = Response>(session: SessionContextValue, url: string, successfulStatus: number, params?: RequestInit) {
    return this.request<ResponseType>(session, url, RequestType.PUT, successfulStatus, params);
  },
  async patch<ResponseType = Response>(session: SessionContextValue, url: string, successfulStatus: number, params?: RequestInit) {
    return this.request<ResponseType>(session, url, RequestType.PATCH, successfulStatus, params);
  },
  async download(session: SessionContextValue, url: string, accept: string, fallbackName: string, successfulStatus: number) {
    const { response, statusCode } = await apiClient
      .get<{ message: string, blob:() => Promise<Blob>, headers: Headers }>(session, url, successfulStatus, {
      headers: { accept },
    });
    if (statusCode === 200 && response?.headers) {
      const parts = response?.headers
        .get('content-disposition')
        ?.split(';')
        .map((part: string) => part.trim()) ?? ['attachment', `filename=${fallbackName}`];
      const blob = await response.blob();
      const a = document.createElement('a');
      const blobUrl = window.URL.createObjectURL(blob);
      a.href = blobUrl;
      a.download = parts[1].split('=')[1].replace(/(^")|("$)/g, '');
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } else {
      throw new Error(response?.message);
    }
  },
};

export default apiClient;
